/**
 * ChatClient — thin wrapper around the SharedWorker.
 */

import type { ServerMessage } from "./types.ts";

const browser = typeof window !== "undefined" && typeof globalThis.document !== "undefined";

// __APP__ may be injected at runtime via a <script> in index.html; access through globalThis
// to avoid a ReferenceError when it is absent.
const APP = (globalThis as unknown as Record<string, unknown>)["__APP__"] as
  | { BACKEND_BASE?: string; API_BASE?: string }
  | undefined;

export interface AuthResponse {
  session_uuid: string;
  user_uuid: string;
  expires_at: string;
}

const API_URL_SSR = "http://localhost:3000";

export class ChatClient {
  private port?: MessagePort;
  private apiUrl: string;

  private messageHandlers: ((msg: ServerMessage) => void)[] = [];
  private connectionHandlers: ((connected: boolean) => void)[] = [];
  private _backendReadyResolve?: () => void;

  constructor() {
    // Allow overriding API base in the browser via a global injected at build/dev time.
    const backendBase = browser
      ? APP?.BACKEND_BASE ?? APP?.API_BASE
      : undefined;
    this.apiUrl = browser
      ? (backendBase ? `${String(backendBase).replace(/\/$/, "")}/api` : "/api")
      : API_URL_SSR;
  }

  init(): void {
    if (!browser) return;

    const backendBase = APP?.BACKEND_BASE ?? APP?.API_BASE ?? undefined;
    const workerUrl = `/assets/worker.js${
      backendBase ? `?backend=${encodeURIComponent(String(backendBase))}` : ""
    }`;

    // Use a static built worker asset at /assets/worker.js (optionally with backend query)
    if (typeof SharedWorker !== "undefined") {
      try {
        const sharedWorker = new SharedWorker(workerUrl);
        this.port = sharedWorker.port;
      } catch {
        // fallback to dedicated worker
        const dedicatedWorker = new Worker(workerUrl);
        const port = {
          postMessage: (data: string) => dedicatedWorker.postMessage(data),
          onmessage: null as ((e: MessageEvent) => void) | null,
          start: () => {},
        } as unknown as MessagePort;
        dedicatedWorker.onmessage = (e: MessageEvent) => port.onmessage?.(e);
        this.port = port;
      }
    } else {
      const dedicatedWorker = new Worker(workerUrl);
      const port = {
        postMessage: (data: string) => dedicatedWorker.postMessage(data),
        onmessage: null as ((e: MessageEvent) => void) | null,
        start: () => {},
      } as unknown as MessagePort;
      dedicatedWorker.onmessage = (e: MessageEvent) => port.onmessage?.(e);
      this.port = port;
    }

    this.port.onmessage = (e: MessageEvent) => {
      let msg: { type: string; payload?: unknown };
      try {
        msg = JSON.parse(e.data as string);
      } catch {
        console.error("[ChatClient] Unparseable worker message", e.data);
        return;
      }

      switch (msg.type) {
        case "connected":
          this.connectionHandlers.forEach((h) => h(true));
          break;
        case "disconnected":
          this.connectionHandlers.forEach((h) => h(false));
          break;
        case "message":
          this.messageHandlers.forEach((h) => h(msg.payload as ServerMessage));
          break;
        case "sessionInvalid":
          localStorage.removeItem("session_uuid");
          localStorage.removeItem("user_uuid");
          localStorage.removeItem("session_expires_at");
          this.connectionHandlers.forEach((h) => h(false));
          this.messageHandlers.forEach((h) =>
            h({ type: "Error", message: "Session expired", code: "SESSION_EXPIRED" })
          );
          break;
        case "backendReady":
        case "backendTimeout":
          this._backendReadyResolve?.();
          this._backendReadyResolve = undefined;
          break;
      }
    };

    this.port.start();
  }

  login = async (authToken?: string): Promise<AuthResponse> => {
    if (!authToken) {
      const res = await fetch(this.apiUrl);
      const data = await res.json();
      authToken = data.anonymous_token;
    }

    const res = await fetch(this.apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: authToken }),
    });

    if (!res.ok) throw new Error("Authentication failed");

    const auth: AuthResponse = await res.json();
    localStorage.setItem("session_uuid", auth.session_uuid);
    localStorage.setItem("user_uuid", auth.user_uuid);
    localStorage.setItem("session_expires_at", auth.expires_at);
    return auth;
  };

  waitForBackend = (): Promise<void> =>
    new Promise<void>((resolve) => {
      this._backendReadyResolve = resolve;
      this.cmd({ type: "waitForBackend" });
    });

  hasValidSession = (): boolean => {
    const uuid = localStorage.getItem("session_uuid");
    const expiresAt = localStorage.getItem("session_expires_at");
    if (!uuid) return false;
    if (!expiresAt) return true;
    return new Date(expiresAt) > new Date();
  };

  connect = (): void => {
    const sessionUuid = localStorage.getItem("session_uuid");
    if (!sessionUuid) throw new Error("No session UUID");
    this.cmd({ type: "connect", sessionUuid });
  };

  disconnect = (): void => {
    this.cmd({ type: "disconnect" });
  };

  send = (message: unknown): void => {
    this.cmd({ type: "send", payload: message });
  };

  private cmd = (c: object): void => {
    if (!this.port) {
      console.warn("[ChatClient] Worker not initialised");
      return;
    }
    this.port.postMessage(JSON.stringify(c));
  };

  onMessage = (handler: (msg: ServerMessage) => void): () => void => {
    this.messageHandlers.push(handler);
    return () => {
      const i = this.messageHandlers.indexOf(handler);
      if (i > -1) this.messageHandlers.splice(i, 1);
    };
  };

  onConnection = (handler: (connected: boolean) => void): () => void => {
    this.connectionHandlers.push(handler);
    return () => {
      const i = this.connectionHandlers.indexOf(handler);
      if (i > -1) this.connectionHandlers.splice(i, 1);
    };
  };

  private s = (): string | null => localStorage.getItem("session_uuid");
  private u = (): string | null => localStorage.getItem("user_uuid");

  getChatList = (): void =>
    this.send({ type: "UsrGetChatList", user_uuid: this.u(), session_uuid: this.s() });

  getChatHistory = (chatUuid: string, before?: Date): void =>
    this.send({
      type: "UsrGetChatHistorySegment",
      chat_uuid: chatUuid,
      created_before: before?.toISOString(),
      session_uuid: this.s(),
    });

  sendMessage = (chatUuid: string, content: string): void =>
    this.send({ type: "UsrMessage", chat_uuid: chatUuid, content, session_uuid: this.s() });

  getChatMembers = (chatUuid: string): void =>
    this.send({ type: "UsrGetChatMembers", chat_uuid: chatUuid, session_uuid: this.s() });

  getChatInfo = (chatUuid: string): void =>
    this.send({ type: "UsrGetChatInfo", chat_uuid: chatUuid, session_uuid: this.s() });

  getUserInfo = (userUuid?: string): void => {
    const t = userUuid ?? this.u();
    if (t) this.send({ type: "UsrGetUserInfo", user_uuid: t, session_uuid: this.s() });
  };

  getSignatureInfo = (signatureUuid: string): void =>
    this.send({
      type: "UsrGetSignatureInfo",
      signature_uuid: signatureUuid,
      session_uuid: this.s(),
    });

  createSignature = (name: string, avatarUrl?: string): void =>
    this.send({ type: "UsrReqNewSignature", name, avatar_url: avatarUrl, session_uuid: this.s() });
}
